<template>
  <v-navigation-drawer
    v-model="isPaneOpened"
    class="AIAssistant__pane"
    :scrim="true"
    temporary
    persistent
    location="right"
    :width="860"
    data-locator="ai-assistant-pane"
  >
    <div class="pane-wrapper">
      <AIAssistantPaneSidebar 
        :conversations-menu-opened="conversationsMenuOpened"
        @toggle-conversations-menu="onToggleConversationsMenu"
        @new-conversation="onNewConversation"
      />
      <div class="pane-main">
        <AIAssistantPaneHeader
          :user-name="profile.firstName"
          @close-pane="closePane"
        />
        <div class="pane-content d-flex flex-column flex-grow-1 overflow-y-auto pa-6 pb-0">
          <AIAssistantMessages
            v-if="messages.length"
            :messages="messages"
            :post-prompt-pending="postPromptPending"
          />
          <AIAssistantEmptyState v-else />
        </div>
        <div class="pane-footer pa-6 position-relative">
        <div class="py-2 d-flex justify-end">
          <AIAssistantPaneHelp />
        </div>
          <AIAssistantPaneForm
            :cleared="!postPromptPending"
            :disabled="postPromptPending"
            @post-prompt="onPostPrompt"
          />
          <AIAssistantRemainingCredits 
            :credits="credits"
            :credits-label="`${TRANSLATION_KEY_PREFIX}.remainingCreditsLabel`"
            :credits-pending="creditsPending"
          />
        </div>
      </div>
    </div>
  </v-navigation-drawer>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex'

import AIAssistantEmptyState from '@/common/ai-assistant/AIAssistantEmptyState.vue'
import AIAssistantMessages from '@/common/ai-assistant/AIAssistantMessages.vue'
import AIAssistantPaneForm from '@/common/ai-assistant/AIAssistantPaneForm.vue'
import AIAssistantPaneHeader from '@/common/ai-assistant/AIAssistantPaneHeader.vue'
import AIAssistantPaneSidebar from '@/common/ai-assistant/AIAssistantPaneSidebar.vue'
import AIAssistantRemainingCredits from '@/common/ai-assistant/AIAssistantRemainingCredits.vue'
import { toast } from '@/common/utils/toast'
import { AIAssistantActionTypes } from '@/store/modules/ai-assistant/action_types'
import { AIAssistantMutationTypes } from '@/store/modules/ai-assistant/mutation_types'

import AIAssistantPaneHelp from './AIAssistantPaneHelp.vue'


const TRANSLATION_KEY_PREFIX = 'common.ai-assistant.AIAssistantPane'

type DataType = {
  TRANSLATION_KEY_PREFIX: string,
  messages: Array<any>,
  conversationsMenuOpened: boolean,
}

export default defineComponent({
  name: 'AIAssistantPane.vue',
  emits: ['close'],
  components: {
    AIAssistantEmptyState,
    AIAssistantPaneHeader,
    AIAssistantMessages,
    AIAssistantPaneForm,
    AIAssistantPaneSidebar,
    AIAssistantRemainingCredits,
    AIAssistantPaneHelp,
  },
  data (): DataType {
    return {
      TRANSLATION_KEY_PREFIX,
      messages: [],
      conversationsMenuOpened: true,
    }
  },
  computed: {
    ...mapGetters('room', ['roomMnemo']),
    ...mapState('aiAssistant', ['isPaneOpened', 'conversationActiveId', 'credits', 'creditsPending', 'postPromptPending']),
    ...mapState('user', ['profile']),
    shouldBlockPaneBeingClosed (): boolean {
      return this.postPromptPending
    },
    shouldDisplayEmptyState (): boolean {
      return this.messages.length === 0 && !this.postPromptPending
    },
  },
  methods: {
    ...mapActions('aiAssistant', [ AIAssistantActionTypes.POST_PROMPT, AIAssistantActionTypes.GET_CONVERSATION_MESSAGES, AIAssistantActionTypes.GET_CONVERSATIONS ]),
    ...mapMutations('aiAssistant', [
      AIAssistantMutationTypes.DECREMENT_CREDITS_REMAINING,
      AIAssistantMutationTypes.SET_CREDITS,
      AIAssistantMutationTypes.SET_PANE_OPENED,
      AIAssistantMutationTypes.SET_POST_PROMPT_PENDING,
      AIAssistantMutationTypes.SET_CONVERSATION_ACTIVE_ID,
    ]),
    closePane () {
      if (this.shouldBlockPaneBeingClosed) return

      this.SET_PANE_OPENED(false)
      this.setInitialState()
    },
    setInitialState () {
      this.SET_POST_PROMPT_PENDING(false)
      this.SET_CREDITS({"remaining": 5, "total": 5, "renewalDate": ''})
    },
    async getConversationMessages (conversationId: string) {
      try {
        const messages = await this.GET_CONVERSATION_MESSAGES(conversationId)
        this.messages = messages
      } catch (error) {
        console.error('Error fetching conversation messages:', error)
        toast.error(this.$t(`${TRANSLATION_KEY_PREFIX}.getConversationMessagesError`))
      }
    },
    async onPostPrompt (prompt: string) {
       const isFirstMessage = !this.conversationActiveId
       this.messages.push({ prompt })
      try {
        const response = await this.POST_PROMPT({
          mnemo: this.roomMnemo, 
          prompt: prompt,
          conversationId: this.conversationActiveId || null,
        })

        this.messages[this.messages.length - 1] = { 
          ...this.messages[this.messages.length - 1], 
          ...response.message,
          sources: response.sources,
        }
        
        // If it's the first message of a new conversation, refresh the conversations list to include it
        if (isFirstMessage) {
          this.SET_CONVERSATION_ACTIVE_ID(response.message.conversationId.toString())
        }
        
        this.DECREMENT_CREDITS_REMAINING()
      } catch (error) {
        console.error('Error posting prompt:', error)
        toast.error(this.$t(`${TRANSLATION_KEY_PREFIX}.postPromptError`))
      }
    },
    async scrollToLastItem (itemClass: string, scrollOptions: ScrollIntoViewOptions) {
      await this.$nextTick()
      const itemsHtmlElements = document.getElementsByClassName(itemClass)

      if (itemsHtmlElements.length > 0) {
        const lastItem = itemsHtmlElements[itemsHtmlElements.length - 1]
        lastItem.scrollIntoView(scrollOptions)
      }
    },
    onToggleConversationsMenu (isOpened: boolean) {
      this.conversationsMenuOpened = isOpened
    },
    onNewConversation () {
      this.SET_CONVERSATION_ACTIVE_ID(null)
      this.setInitialState()
    },
  },
  watch: {
    conversationActiveId: {
      async handler (newVal:string) {
        if (newVal) {
          await this.getConversationMessages(newVal)
        } else {
          this.messages = []
        }
      },
      immediate: true,
    },
    messages: {
      handler() {
        this.$nextTick(() => {
          this.scrollToLastItem('app-chat-message-container', { behavior: 'smooth', block: 'end' })
        })
      },
      deep: true,
    },
    isPaneOpened: {
      handler (newVal:boolean) {
        if (!newVal) {
          this.setInitialState()
        }
      },
      immediate: true,
    },
  },
})
</script>

<style scoped lang="scss">
.AIAssistant__pane {
  background-color: #F7F9FA;
  border-left: 1px solid #DFE3E8;
  line-height: 20px;
  z-index: 1020 !important; /* To be above the ChecklistPaneFiles zIndex 1018 */

  .pane-wrapper {
    height: 100%;
    display: flex;
    flex-direction: row;
  }

  .pane-sidebar {
    width: 340px;
    background-color: white;
    border-right: 1px solid #DFE3E8;
    flex-shrink: 0;
  }

  .pane-main {
    width: 540px;
    height: 100%;
    display: flex;
    flex-direction: column;
    flex-grow: 1;
  }

  .pane-header {
    align-items: center;
    display: flex;
    justify-content: space-between;
  }

  .pane-close-button {
    width: 28px;
    height: 28px;
    min-width: 28px;
    min-height: 28px;
    border-radius: 50%;

    &:hover:not(:disabled) {
      background-color: rgba(0, 23, 46, 0.05) !important;
    }
    &:active:not(:disabled) {
      background-color: rgba(0, 23, 46, 0.10) !important;
    }
    &:disabled {
      opacity: 0.5;
      pointer-events: none;
    }
  }

  .ai-remaining-credits {
    position: absolute;
    bottom: 32px;
    left: 32px;
  }
}
</style>

<style lang="scss">
@mixin app-button-disabled {
  &:disabled {
    background-color: #EDF0F2 !important;
    color: #A0AFC0 !important;
    background-image: none !important;
  }
}

.AIAssistant__pane {
  font-family: 'Lato', sans-serif;
  color: #00172E;

  .app-button--surface-brand {
    background: #00172E !important;
    color: white;

    &:hover:not(:disabled) {
      background-color: #103051 !important;
    }
    &:active:not(:disabled) {
      background-color: #2A476A !important;
    }
    @include app-button-disabled;
  }
}

.AIAssistant__tooltip {
  .v-overlay__content {
    background-color: #00172E;
    border-radius: 9px;
    color: white !important;
    font-family: 'Lato', sans-serif;
    font-size: 14px;
    padding: 0.5rem 0.75rem;
  }
}
</style>